import { useState, useEffect, useRef } from 'react';
import { useDemoCtx } from '../DemoContext';
import { calcPolygonAreaM2, calcCentroid, type SiteFormData } from '../demoStorage';

export function SiteRegForm() {
    const { pendingPolygon, registerSiteFormGetter } = useDemoCtx();

    /* ── polygon-derived initial values (computed once at mount) ── */
    const polyAreaM2 = pendingPolygon ? Math.round(calcPolygonAreaM2(pendingPolygon)) : 0;
    const centroid   = pendingPolygon ? calcCentroid(pendingPolygon) : null;

    /* ── form state ── */
    const [openSecs, setOpenSecs] = useState<Record<string, boolean>>({ basic: true, tree: false, soil: false });
    const [name,         setName]         = useState('');
    const [startDate,    setStartDate]    = useState('2026-01-15');
    const [mgr,          setMgr]          = useState('김관리자');
    const [addr,         setAddr]         = useState(
        () => centroid ? `위도 ${centroid.lat.toFixed(6)}, 경도 ${centroid.lng.toFixed(6)}` : ''
    );
    const [area,         setArea]         = useState(
        () => polyAreaM2 > 0 ? polyAreaM2.toLocaleString() : ''
    );
    const [pyeong,       setPyeong]       = useState(
        () => polyAreaM2 > 0 ? Math.round(polyAreaM2 / 3.3058).toLocaleString() : ''
    );
    const [treeName,     setTreeName]     = useState('');
    const [treeCount,    setTreeCount]    = useState('');
    const [treePlantDate,setTreePlantDate]= useState('');
    const [soilType,     setSoilType]     = useState('양토');
    const [ph,           setPh]           = useState('6.5');
    const [initialMoist, setInitialMoist] = useState('65');
    const [soilDepth,    setSoilDepth]    = useState('30');
    const [drainage,     setDrainage]     = useState('양호');
    const [memo,         setMemo]         = useState('');

    const toggle = (k: string) => setOpenSecs(p => ({ ...p, [k]: !p[k] }));

    /* ── stable ref that always holds the latest form values ── */
    const formDataRef = useRef<SiteFormData>({} as SiteFormData);
    formDataRef.current = { name, startDate, mgr, addr, area, pyeong, treeName, treeCount, treePlantDate, soilType, ph, initialMoist, soilDepth, drainage, memo };

    /* ── register getter for SiteRegFooter's save button ── */
    useEffect(() => {
        registerSiteFormGetter(() => formDataRef.current);
        return () => registerSiteFormGetter(null);
    }, [registerSiteFormGetter]);

    /* ── area field: keep pyeong in sync when user edits ㎡ ── */
    const handleAreaChange = (v: string) => {
        setArea(v);
        const num = parseFloat(v.replace(/,/g, ''));
        setPyeong(isNaN(num) ? '' : Math.round(num / 3.3058).toLocaleString());
    };

    return (
        <div>
            {/* 폴리곤 상태 표시 */}
            {pendingPolygon ? (
                <div style={{
                    background: 'var(--primary-light)', border: '1.5px solid var(--primary-mid)',
                    borderRadius: 10, padding: 14, marginBottom: 16,
                }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary-dark)', marginBottom: 6 }}>
                        구역 설정 완료
                    </div>
                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>
                            <span style={{ color: 'var(--text3)' }}>꼭지점</span>&nbsp;
                            <strong>{pendingPolygon.length}개</strong>
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>
                            <span style={{ color: 'var(--text3)' }}>면적</span>&nbsp;
                            <strong>{polyAreaM2.toLocaleString()} ㎡</strong>
                            <span style={{ color: 'var(--text3)' }}> ({Math.round(polyAreaM2 / 3.3058).toLocaleString()}평)</span>
                        </div>
                        {centroid && (
                            <div style={{ fontSize: 11.5, color: 'var(--text2)' }}>
                                <span style={{ color: 'var(--text3)' }}>중심좌표</span>&nbsp;
                                <strong>{centroid.lat.toFixed(5)}, {centroid.lng.toFixed(5)}</strong>
                            </div>
                        )}
                    </div>
                    <div style={{ marginTop: 8, fontSize: 11, color: 'var(--text3)' }}>
                        폴리곤 꼭지점 좌표 {pendingPolygon.length}개 저장됩니다
                    </div>
                </div>
            ) : (
                <div style={{
                    background: 'var(--surface2)', border: '1.5px dashed var(--border2)',
                    borderRadius: 10, padding: 14, marginBottom: 16,
                }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text3)', marginBottom: 2 }}>
                        구역 미설정
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)' }}>
                        지도에서 생육단지 등록 버튼 클릭 후 폴리곤을 먼저 그려주세요
                    </div>
                </div>
            )}

            {/* 기본 정보 */}
            <div className={`acc${openSecs.basic ? ' open' : ''}`}>
                <div className="acc-hd" onClick={() => toggle('basic')}>
                    📋 기본 정보<span className="acc-arrow">▼</span>
                </div>
                {openSecs.basic && (
                    <div className="acc-body">
                        <div className="form-group">
                            <label className="form-label">생육단지명 <span style={{ color: 'var(--danger)' }}>*</span></label>
                            <input className="form-input" placeholder="예: 강남 중앙공원 A구역" value={name} onChange={e => setName(e.target.value)} />
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">관리 시작일</label>
                                <input type="date" className="form-input" value={startDate} onChange={e => setStartDate(e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">담당자</label>
                                <select className="form-select" value={mgr} onChange={e => setMgr(e.target.value)}>
                                    <option>김관리자</option><option>박현장</option><option>이담당</option>
                                </select>
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">주소 (중심좌표)</label>
                            <div style={{ display: 'flex', gap: 7, marginBottom: 7 }}>
                                <input className="form-input" value={addr} onChange={e => setAddr(e.target.value)} style={{ flex: 1 }} placeholder="폴리곤 그리기 후 자동 입력" />
                                <button className="btn btn-secondary btn-sm" onClick={() => centroid && setAddr(`위도 ${centroid.lat.toFixed(6)}, 경도 ${centroid.lng.toFixed(6)}`)}>초기화</button>
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">면적 (㎡)</label>
                                <input className="form-input" value={area} onChange={e => handleAreaChange(e.target.value)} placeholder="폴리곤 그리기 후 자동 계산" />
                            </div>
                            <div className="form-group">
                                <label className="form-label">면적 (평)</label>
                                <input className="form-input" value={pyeong} readOnly placeholder="자동 계산" />
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* 식재 정보 */}
            <div className={`acc${openSecs.tree ? ' open' : ''}`}>
                <div className="acc-hd" onClick={() => toggle('tree')}>
                    🌲 식재 정보<span className="acc-arrow">▼</span>
                </div>
                {openSecs.tree && (
                    <div className="acc-body">
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr>
                                {['수종명', '수량', '식재일', ''].map(h => (
                                    <th key={h} style={{ fontSize: 10, color: 'var(--text3)', padding: '6px 8px', textAlign: 'left', borderBottom: '1.5px solid var(--border)', fontWeight: 700 }}>{h}</th>
                                ))}
                            </tr></thead>
                            <tbody><tr>
                                <td style={{ padding: '7px 8px' }}><input className="form-input" placeholder="왕벚나무" style={{ height: 30 }} value={treeName} onChange={e => setTreeName(e.target.value)} /></td>
                                <td style={{ padding: '7px 8px' }}><input type="number" className="form-input" placeholder="0" style={{ height: 30, width: 70 }} value={treeCount} onChange={e => setTreeCount(e.target.value)} /></td>
                                <td style={{ padding: '7px 8px' }}><input type="date" className="form-input" style={{ height: 30 }} value={treePlantDate} onChange={e => setTreePlantDate(e.target.value)} /></td>
                                <td style={{ padding: '7px 8px' }}><button className="btn btn-ghost btn-sm">✕</button></td>
                            </tr></tbody>
                        </table>
                        <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }}>＋ 수종 추가</button>
                    </div>
                )}
            </div>

            {/* 토양 정보 */}
            <div className={`acc${openSecs.soil ? ' open' : ''}`}>
                <div className="acc-hd" onClick={() => toggle('soil')}>
                    🪨 토양 정보<span className="acc-arrow">▼</span>
                </div>
                {openSecs.soil && (
                    <div className="acc-body">
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">토양 유형</label>
                                <select className="form-select" value={soilType} onChange={e => setSoilType(e.target.value)}>
                                    <option>양토</option><option>사질토</option><option>점토</option><option>혼합</option>
                                </select>
                            </div>
                            <div className="form-group">
                                <label className="form-label">pH</label>
                                <input type="number" className="form-input" value={ph} onChange={e => setPh(e.target.value)} min={3} max={10} step={0.1} />
                            </div>
                        </div>
                        <div className="form-row">
                            <div className="form-group">
                                <label className="form-label">초기 수분율 (%)</label>
                                <input type="number" className="form-input" value={initialMoist} onChange={e => setInitialMoist(e.target.value)} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">토양 깊이 (cm)</label>
                                <input type="number" className="form-input" value={soilDepth} onChange={e => setSoilDepth(e.target.value)} />
                            </div>
                        </div>
                        <div className="form-group">
                            <label className="form-label">배수성</label>
                            <select className="form-select" value={drainage} onChange={e => setDrainage(e.target.value)}>
                                <option>양호</option><option>보통</option><option>불량</option>
                            </select>
                        </div>
                        <div className="form-group">
                            <label className="form-label">메모</label>
                            <textarea className="form-textarea" placeholder="특이사항 기재" value={memo} onChange={e => setMemo(e.target.value)} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
